import { NSARouterMakerResult } from "namirasoft-account-react";
import { PriceOperation } from "namirasoft-core";
import { InferencerType, UsageBucketType, UsageDashboardBucketRow } from "namirasoft-inference";
import { NSBoxDateRange, NSBoxEnum, NSChartBar, NSColumn, NSLoading, NSRow, NSTitle } from "namirasoft-site-react";
import { Component, createRef } from "react";
import { InferenceRouterProps } from "../InferenceRouterProps";

export enum UsageBucket
{
    Hour = "Hour",
    Day = "Day",
}

export enum UsageMetric
{
    Count = "Count",
    TotalToken = "TotalToken",
    TotalTokenInput = "TotalTokenInput",
    TotalTokenOutput = "TotalTokenOutput",
    TotalCost = "TotalCost",
    TotalCostInput = "TotalCostInput",
    TotalCostOutput = "TotalCostOutput",
}

function metricField(metric: UsageMetric): keyof UsageDashboardBucketRow
{
    switch (metric)
    {
        case UsageMetric.Count: return "count";
        case UsageMetric.TotalToken: return "total_token";
        case UsageMetric.TotalTokenInput: return "total_token_input";
        case UsageMetric.TotalTokenOutput: return "total_token_output";
        case UsageMetric.TotalCost: return "total_cost";
        case UsageMetric.TotalCostInput: return "total_cost_input";
        case UsageMetric.TotalCostOutput: return "total_cost_output";
    }
}

export enum UsageWinnerFilter
{
    All = "All",
    WinnersOnly = "WinnersOnly",
    NonWinnersOnly = "NonWinnersOnly",
}

interface UsageChartProps extends NSARouterMakerResult, InferenceRouterProps
{
    inferencer_type?: InferencerType | null;
    inferencer_id?: string | null;
    title?: string;
    default_winner_filter?: UsageWinnerFilter;
    default_metric?: UsageMetric | UsageMetric[];
}

interface UsageChartState
{
    range: { from: Date; to: Date };
    bucket: UsageBucket;
    metrics: UsageMetric[];
    winner_filter: UsageWinnerFilter;
    buckets: UsageDashboardBucketRow[];
    labels: string[];
    loading: boolean;
}

const METRIC_COLORS: string[] = ["#7583E6", "#3AA498", "#DB7E46", "#8C4BCB", "#E6A23C", "#F56C6C", "#909399"];

function startOfHour(d: Date): Date
{
    let x = new Date(d);
    x.setMinutes(0, 0, 0);
    return x;
}

function startOfDay(d: Date): Date
{
    let x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
}

function formatHour(d: Date): string
{
    let h = d.getHours();
    let suffix = h >= 12 ? "PM" : "AM";
    let hh = h % 12 === 0 ? 12 : h % 12;
    return `${hh}${suffix}`;
}

function formatDay(d: Date): string
{
    return d.toLocaleString("en-US", { month: "short", day: "numeric" });
}

function metricTitle(metric: UsageMetric): string
{
    switch (metric)
    {
        case UsageMetric.Count: return "Requests";
        case UsageMetric.TotalToken: return "Total Tokens";
        case UsageMetric.TotalTokenInput: return "Input Tokens";
        case UsageMetric.TotalTokenOutput: return "Output Tokens";
        case UsageMetric.TotalCost: return "Total Cost";
        case UsageMetric.TotalCostInput: return "Input Cost";
        case UsageMetric.TotalCostOutput: return "Output Cost";
    }
}

function winnerFilterTitle(f: UsageWinnerFilter): string
{
    switch (f)
    {
        case UsageWinnerFilter.All:
            return "All";
        case UsageWinnerFilter.WinnersOnly:
            return "Winners Only";
        case UsageWinnerFilter.NonWinnersOnly:
            return "Non-Winners Only";
    }
}

function winnerFilterToBoolean(f: UsageWinnerFilter): boolean | null
{
    switch (f)
    {
        case UsageWinnerFilter.WinnersOnly:
            return true;
        case UsageWinnerFilter.NonWinnersOnly:
            return false;
        case UsageWinnerFilter.All:
        default:
            return null;
    }
}

function isCostMetric(metric: UsageMetric): boolean
{
    return metric === UsageMetric.TotalCost || metric === UsageMetric.TotalCostInput || metric === UsageMetric.TotalCostOutput;
}

function defaultRange(): { from: Date; to: Date }
{
    let to = new Date();
    let from = new Date(to.getTime() - 7 * 24 * 60 * 60 * 1000);
    return { from, to };
}

export default class UsageChart extends Component<UsageChartProps, UsageChartState>
{
    override state: UsageChartState;

    private NSBoxDateRange_Period = createRef<NSBoxDateRange>();
    private NSBoxEnum_Bucket = createRef<NSBoxEnum<UsageBucket>>();
    private NSBoxEnum_Metric = createRef<NSBoxEnum<UsageMetric>>();
    private NSBoxEnum_WinnerFilter = createRef<NSBoxEnum<UsageWinnerFilter>>();

    constructor(props: UsageChartProps)
    {
        super(props);
        let metrics: UsageMetric[];
        if (Array.isArray(props.default_metric))
            metrics = props.default_metric.length > 0 ? props.default_metric : [UsageMetric.Count];
        else if (props.default_metric)
            metrics = [props.default_metric];
        else
            metrics = [UsageMetric.Count];
        this.state = {
            range: defaultRange(),
            bucket: UsageBucket.Day,
            metrics,
            winner_filter: props.default_winner_filter ?? UsageWinnerFilter.WinnersOnly,
            buckets: [],
            labels: [],
            loading: false,
        };
    }

    override componentDidMount(): void
    {
        this.reload();
    }

    override componentDidUpdate(prev: UsageChartProps): void
    {
        if (prev.inferencer_id !== this.props.inferencer_id || prev.inferencer_type !== this.props.inferencer_type)
            this.reload();
    }

    private getWindow(range: { from: Date; to: Date }, bucket: UsageBucket): { start: Date; end: Date; bucket: UsageBucketType; slots: Date[]; slotOf: (d: Date) => number; label: (d: Date) => string }
    {
        let end = range.to;
        let slots: Date[] = [];
        if (bucket === UsageBucket.Hour)
        {
            let start = startOfHour(range.from);
            let count = Math.max(1, Math.ceil((end.getTime() - start.getTime()) / (60 * 60 * 1000)));
            for (let i = 0; i < count; i++)
                slots.push(new Date(start.getTime() + i * 60 * 60 * 1000));
            return {
                start,
                end,
                bucket: "Hour" as UsageBucketType,
                slots,
                slotOf: (d) => Math.floor((startOfHour(d).getTime() - start.getTime()) / (60 * 60 * 1000)),
                label: formatHour,
            };
        }
        let start = startOfDay(range.from);
        let lastDay = startOfDay(end);
        let days = Math.max(1, Math.floor((lastDay.getTime() - start.getTime()) / (24 * 60 * 60 * 1000)) + 1);
        for (let i = 0; i < days; i++)
            slots.push(new Date(start.getTime() + i * 24 * 60 * 60 * 1000));
        return {
            start,
            end,
            bucket: "Day" as UsageBucketType,
            slots,
            slotOf: (d) => Math.floor((startOfDay(d).getTime() - start.getTime()) / (24 * 60 * 60 * 1000)),
            label: formatDay,
        };
    }

    private async reload(): Promise<void>
    {
        let window = this.getWindow(this.state.range, this.state.bucket);

        this.setState({ loading: true });
        try
        {
            let res = await this.props.servers.inference.usage.Dashboard(
                this.props.inferencer_type ?? null,
                this.props.inferencer_id ?? null,
                winnerFilterToBoolean(this.state.winner_filter),
                window.start.toISOString(),
                window.end.toISOString(),
                window.bucket,
            );
            let labels = window.slots.map(window.label);
            this.setState({ buckets: res.buckets, labels, loading: false });
        }
        catch
        {
            this.setState({ loading: false });
        }
    }

    private buildDataFor(metric: UsageMetric): number[]
    {
        let window = this.getWindow(this.state.range, this.state.bucket);
        let cost = isCostMetric(metric);
        let data = new Array<number>(window.slots.length).fill(0);
        for (let b of this.state.buckets)
        {
            let d = new Date(b.bucket.replace(" ", "T") + "Z");
            let idx = window.slotOf(d);
            if (idx >= 0 && idx < data.length)
            {
                let raw = Number((b as any)[metricField(metric)] ?? 0);
                data[idx] += cost ? PriceOperation.millicent_to_fiat(raw, 4) : raw;
            }
        }
        return data;
    }

    override render()
    {
        let { range, bucket, metrics, winner_filter, labels, loading } = this.state;
        let window = this.getWindow(range, bucket);
        let datasets = metrics.map((m, i) => ({
            label: metricTitle(m),
            data: this.buildDataFor(m),
            color: METRIC_COLORS[i % METRIC_COLORS.length],
        }));
        let allValues = datasets.flatMap(d => d.data);
        let max = Math.max(1, ...allValues) * 1.1;
        let allCost = metrics.length > 0 && metrics.every(isCostMetric);

        return (
            <NSColumn style={{ width: "100%", marginTop: 16, gap: 8 }}>
                <NSRow style={{ alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
                    <NSTitle title={this.props.title ?? "Request Usage Over Time"} />
                    <NSRow style={{ gap: 8, alignItems: "center" }}>
                        <NSBoxEnum<UsageMetric>
                            ref={this.NSBoxEnum_Metric}
                            title="Metric"
                            hideHeader
                            required={false}
                            multiple={true}
                            defaultValue={metrics}
                            getEnumObject={() => UsageMetric}
                            getTitle={(v) => metricTitle(v as UsageMetric)}
                            onChanged={(box) =>
                            {
                                try
                                {
                                    let next = box.getValueEnumAll(UsageMetric, UsageMetric.Count);
                                    this.setState({ metrics: next });
                                } catch (error)
                                {
                                    this.setState({ metrics: [] });
                                }
                            }}
                        />
                        <NSBoxEnum<UsageWinnerFilter>
                            ref={this.NSBoxEnum_WinnerFilter}
                            title="Winners"
                            hideHeader
                            required
                            multiple={false}
                            defaultValue={winner_filter}
                            getEnumObject={() => UsageWinnerFilter}
                            getTitle={(v) => winnerFilterTitle(v as UsageWinnerFilter)}
                            onChanged={(box) =>
                            {
                                let next = box.getValueEnumOne(UsageWinnerFilter, UsageWinnerFilter.WinnersOnly, false) ?? UsageWinnerFilter.WinnersOnly;
                                this.setState({ winner_filter: next }, () => this.reload());
                            }}
                        />
                        <NSBoxDateRange
                            ref={this.NSBoxDateRange_Period}
                            title="Period"
                            hideHeader
                            required
                            defaultValue={range}
                            onChanged={(box) =>
                            {
                                let next = box.getValue(false);
                                if (!next) return;
                                this.setState({ range: next }, () => this.reload());
                            }}
                        />
                        <NSBoxEnum<UsageBucket>
                            ref={this.NSBoxEnum_Bucket}
                            title="Bucket"
                            hideHeader
                            required
                            multiple={false}
                            defaultValue={bucket}
                            getEnumObject={() => UsageBucket}
                            getTitle={(v) => v}
                            onChanged={(box) =>
                            {
                                let next = box.getValueEnumOne(UsageBucket, UsageBucket.Day, false) ?? UsageBucket.Day;
                                this.setState({ bucket: next }, () => this.reload());
                            }}
                        />
                    </NSRow>
                </NSRow>
                {
                    loading
                        ? <NSLoading title="Loading usage ..." />
                        : <div style={{ position: "relative", height: 280, width: "100%" }}>
                            {
                                metrics.length > 0 &&
                                <NSChartBar
                                    labels={labels}
                                    dataset={datasets}
                                    range={{ min: 0, max }}
                                    yGrid
                                    height={280}
                                    xTitle={window.bucket === "Hour" ? "Hour" : "Day"}
                                    formatValue={(v) => allCost ? `$${(v).toFixed(4)}` : Math.round(v).toString()}
                                />
                            }
                        </div>
                }
            </NSColumn>
        );
    }
}